import { makeAutoObservable } from "mobx"
import ms from "ms"

import {
	authenticateInKemsuWebSystem,
	AuthenticateResultType,
	AuthenticateType, clearUserSessionData,
	getErrorMessageFromObjects,
	getStudentUserInfo,
	getUserAccessRestrictionStatus,
	getUserInfo,
	IStudentUserInfo,
	IUserInfo,
	SecurityObject,
	UserAccessRestrictionCode,
	UserAccessRestrictionQueryParams
} from "~/domain/user-session"
import { AccessError } from "~/domain/user-session/AccessError"
import { hasAccessTo } from "~/domain/user-session/api/hasAccessTo"
import { api } from "~/shared/api"
import { execKemsuWebSystemPage } from "~/shared/api/kemsuweb"
import { accessTokenLifetime } from "~/shared/config"
import { getErrorMessage, isStandalone, throwErrorFrom } from "~/shared/lib/utils"

export enum AuthenticateStatus {
	NONE,
	IN_PROGRESS,
	CODE_SENT,
	COMPLETED,
}

// Время жизни токена с запасом в 1 минуту
const ttlMs = ms(accessTokenLifetime) - 60 * 1000

// Макс. время бездействия пользователя
const maxInactivityTime = 4 * 60 * 60 * 1000

class UserSessionStore {
	private _userInfo: IUserInfo | null = getUserInfo()
	private _studentUserInfo: IStudentUserInfo | null = getStudentUserInfo()
	private _authStatus = AuthenticateStatus.NONE
	private _prevAuthStatus = AuthenticateStatus.NONE
	private _authIp = localStorage.getItem("auth_ip") ?? ""
	private _isIpChanged = false

	#authAbortCtrl: AbortController | null = null
	#password: string | null = null // для аутентификации на xiais
	#refreshPromise: Promise<void> | null = null
	#initialRefreshTokenPromise: Promise<boolean> | null = null
	#lastTokenRefreshing: Date | null = null
	#refreshTokenJobInProcess = false
	#lastActivityDate: Date
	#timerId: ReturnType<typeof setTimeout> | null = null
	#timerReject: (() => void) | null = null

	constructor() {
		makeAutoObservable(this)

		const lastActivityDate = localStorage.getItem("last_activity")
		this.#lastActivityDate = lastActivityDate ? new Date(lastActivityDate) : new Date()

		window.addEventListener("storage", e => {
			if (!e.key || e.key === "userInfo") {
				location.replace("/main")
			}
		})

		document.addEventListener("visibilitychange", async () => {

			if (document.visibilityState === "hidden") {
				this.stopRefreshTokenJob()
				return
			}

			if (this._userInfo) {
				this.scheduleRefreshTokenJob()
			} else {
				this.stopRefreshTokenJob()
			}
		})

		window.addEventListener("beforeunload", () => {
			this.stopRefreshTokenJob()
		})

		/*this.#refreshTokenWorker.addEventListener("message", (e: MessageEvent) => {
			if (e.data.type === "error") {
				this.stopRefreshTokenJob()
			}
		})*/
	}

	get isIpChanged() {
		return this._isIpChanged
	}

	get authIp() {
		return this._authIp
	}

	get userInfo(): IUserInfo | null {
		return this._userInfo
	}

	get studentUserInfo(): IStudentUserInfo | null {
		return this._studentUserInfo
	}

	get authStatus() {
		return this._authStatus
	}

	get prevAuthStatus() {
		return this._prevAuthStatus
	}

	private set authStatus(status: AuthenticateStatus) {
		this._prevAuthStatus = this._authStatus
		this._authStatus = status
	}

	private set userInfo(userInfo: IUserInfo) {
		this._userInfo = userInfo
		localStorage.setItem("userInfo", JSON.stringify(userInfo))
	}

	private set studentUserInfo(studentUserInfo: IStudentUserInfo) {
		this._studentUserInfo = studentUserInfo
		localStorage.setItem("paUserInfo", JSON.stringify(studentUserInfo))
	}

	reset() {
		this._userInfo = null
		this._studentUserInfo = null
		localStorage.removeItem("userInfo")
		localStorage.removeItem("paUserInfo")
		localStorage.removeItem("last_token_refreshing")
	}

	cancelAuthenticate() {
		if (
			this._authStatus === AuthenticateStatus.IN_PROGRESS ||
			this._authStatus === AuthenticateStatus.CODE_SENT
		) {
			this.#authAbortCtrl?.abort()

			this.authStatus = AuthenticateStatus.NONE
			this.#password = null
		}
	}

	private onAuthenticateCodeSent() {
		this.authStatus = AuthenticateStatus.CODE_SENT
	}

	private onAuthenticateComplete(userInfo: IUserInfo) {
		this.userInfo = userInfo
		this.authStatus = AuthenticateStatus.COMPLETED
	}

	private async onAuthenticateError(err: unknown) {

		if (this._authStatus === AuthenticateStatus.IN_PROGRESS) {

			if (this._prevAuthStatus === AuthenticateStatus.CODE_SENT) {
				this.authStatus = AuthenticateStatus.CODE_SENT
			} else {
				this.#password = null
				this.authStatus = AuthenticateStatus.NONE
			}
		}

		await throwErrorFrom(err)
	}

	async authenticate(
		login: string,
		passwordOrCode: string,
		accessTokenLifetime: string | undefined = undefined,
		authType: AuthenticateType = AuthenticateType.DEFAULT
	) {
		if (this._authStatus === AuthenticateStatus.IN_PROGRESS)
			return AuthenticateResultType.IN_PROGRESS

		this.authStatus = AuthenticateStatus.IN_PROGRESS

		try {
			this.#authAbortCtrl = new AbortController()

			const response = await api.post(
				`/security/auth/${authType === AuthenticateType.DEFAULT ? "auth" : "auth-by-code"}`,
				{
					login,
					[authType === AuthenticateType.DEFAULT
						? "password"
						: "code"]: passwordOrCode,
					lifetime: accessTokenLifetime
				},
				{
					signal: this.#authAbortCtrl.signal
				}
			)

			if (response.data.twoFactorAuthEnabled) {
				this.#password = passwordOrCode
				this.onAuthenticateCodeSent()
			} else {
				const userInfo = response.data.userInfo as IUserInfo

				if (userInfo.userType === "обучающийся") {
					await this.refreshStudentUserInfo()
				}

				if (!isStandalone()) {
					let password = passwordOrCode

					if (authType === AuthenticateType.TWO_FACTOR) {
						// эта проверка, по идеи, не должна проходить
						if (!this.#password)
							throw new Error(
								"Не удалось выполнить синхронизацию с xiais.kemsu.ru"
							)
						password = this.#password
						this.#password = null
					}

					await authenticateInKemsuWebSystem(login, password)
				}

				this.#updateTokenDates()

				this.stopRefreshTokenJob()
				this.scheduleRefreshTokenJob()

				localStorage.setItem("auth_ip", await this.getCurrentIp())

				this.onAuthenticateComplete(userInfo)
			}

			return response.data.twoFactorAuthEnabled
				? AuthenticateResultType.TWO_FACTOR
				: AuthenticateResultType.SUCCESS
		} catch (err) {
			await this.onAuthenticateError(err)
			return AuthenticateResultType.ERROR
		}
	}

	async refreshUserInfo() {
		const response = await api.get("security/users")
		this.userInfo = response.data
	}

	async refreshStudentUserInfo() {
		const response = await api.get("person-area-user-info")
		this.studentUserInfo = response.data.userInfo
	}

	async checkAccess(params: UserAccessRestrictionQueryParams = {}, defErrorMessage?: string | React.ReactElement<any, string | React.JSXElementConstructor<any>>) {

		const { code, objectIndex } = await getUserAccessRestrictionStatus(params)

		if (code !== UserAccessRestrictionCode.ACCESS_IS_ALLOWED) {

			const errorMessage = getErrorMessageFromObjects(params.objects, objectIndex, defErrorMessage)

			throw new AccessError(code, errorMessage)
		}

	}

	#clearUserSessionData() {
		clearUserSessionData()
	}

	#logoutKemsuWebSystem() {
		return execKemsuWebSystemPage("dekanat/restricted/logoff.htm")
	}

	async logout() {
		this.#clearUserSessionData()

		if (!isStandalone()) {
			await this.#logoutKemsuWebSystem()
		}

		await api.post("/security/auth/clear-tokens")

		location.replace("/main")
	}

	#isTokenRefreshedRecently() {

		if (this.#lastTokenRefreshing) {
			const curDate = new Date()
			const diff = (+curDate) - (+this.#lastTokenRefreshing)

			if (diff < ttlMs) {
				return true
			}
		}

		return false
	}

	async #refreshToken() {

		if (this.#isTokenRefreshedRecently()) {
			return true
		}

		console.log("Обновление токена")

		const isRefreshing = this.#refreshPromise !== null

		if (!isRefreshing) {
			this.#refreshPromise = api.post("/security/auth/refresh-token", { useCookies: true })
		}

		try {
			await this.#refreshPromise

			if (!isRefreshing) {
				this.#lastTokenRefreshing = new Date()
			}

			this.#updateTokenDates()

			return true
		} catch {
			return false
		} finally {
			this.#refreshPromise = null
		}
	}

	isTokenRefreshing(): boolean {
		return this.#refreshPromise !== null
	}

	async waitForRefreshToken() {
		if (this.#refreshPromise) {
			try {
				await this.#refreshPromise
				return true
			} catch {
				return false
			}
		}

		return false
	}

	async validateToken() {
		try {
			const response = await api.post("/security/auth/validate-token")
			return response.data as boolean
		} catch {
			return false
		}
	}

	async refreshToken() {

		if (this.isTokenRefreshing()) {
			return await this.waitForRefreshToken()
		}

		if (navigator.locks) {

			let result = false

			await navigator.locks.request("refresh_token", async () => {

				const lastTokenRefreshing = localStorage.getItem("last_token_refreshing")

				if (lastTokenRefreshing) {
					this.#lastTokenRefreshing = new Date(lastTokenRefreshing)
				}

				result = await this.#refreshToken()

				if (result) {
					localStorage.setItem("last_token_refreshing", this.#lastTokenRefreshing!.toString())
				} else {
					localStorage.removeItem("last_token_refreshing")
				}
			})

			return result

		} else {
			return await this.#refreshToken()
		}
	}

	async canRefreshToken() {
		try {
			const response = await api.post("/security/auth/can-refresh-token")
			return response.data
		} catch {
			return false
		}
	}

	/*async #refreshTokenJob() {
		try {
			await api.post("refresh-token", { useCookies: true })
			console.log("Токены обновлены")
			return true
		} catch {
			return false
		}
	}*/

	stopRefreshTokenJob() {

		if (!this.#refreshTokenJobInProcess) return

		this.#refreshTokenJobInProcess = false

		if (this.#timerId && this.#timerReject) {
			clearTimeout(this.#timerId)
			this.#timerId = null

			this.#timerReject()
			this.#timerReject = null
		}

		/*this.#refreshTokenWorker.postMessage({ type: "stop" })
		this.#completeRefreshTokenJob?.()*/

		console.log("Обновление токенов остановлено")
	}

	async #initialRefreshTokenJob() {
		const expDateStr = localStorage.getItem("token_exp_date")

		if (expDateStr) {
			const expDate = new Date(expDateStr)

			if (Date.now() > expDate.getTime()) {
				console.log("Начальное обновление токена")
				this.#initialRefreshTokenPromise = this.refreshToken()
				this.#refreshTokenJobInProcess = await this.#initialRefreshTokenPromise
				this.#initialRefreshTokenPromise = null
			}
		}
	}

	async initialRefreshTokenJob(){
		if (navigator.locks) {
			// для корректной работы с несколькими вкладками
			await navigator.locks.request("initial_refresh_token_job", () => this.#initialRefreshTokenJob())
		} else {
			await this.#initialRefreshTokenJob()
		}
	}

	scheduleRefreshTokenJob() {

		if (this.#refreshTokenJobInProcess) return

		this.#refreshTokenJobInProcess = true

		const job = async () => {

			console.log("Запуск периодического обновления токенов...")

			await this.#initialRefreshTokenJob();

			while (this.#refreshTokenJobInProcess) {

				try {

					const resolvers = Promise.withResolvers<void>()

					this.#timerReject = resolvers.reject

					const tokenExpDateStr = localStorage.getItem("token_exp_date")

					if (!tokenExpDateStr) {
						this.stopRefreshTokenJob()
						return
					}

					const expDate = new Date(tokenExpDateStr)
					let prevDate = new Date()

					const handler = () => {

						const curDate = new Date()

						if (curDate > expDate) {
							this.#timerId = null
							resolvers.resolve()
						} else {

							const dt = (+curDate) - (+prevDate)
							let t = 5000

							if (dt > t) {
								t = 2 * t - dt
							}

							this.#timerId = setTimeout(handler, t)
							prevDate = curDate
						}

					}

					this.#timerId = setTimeout(handler, 5000)

					await resolvers.promise

				} catch {
					return
				}

				if (this.isUserInactive()) {
					await this.logout()
				}

				const isRefreshed = await this.refreshToken()

				if (!isRefreshed) {

					// Возможно, обновить не удалось из-за смены IP (динамический IP, VPN)
					/*if (await this.checkIfIpWasChanged()) {
						location.reload()
						return
					}*/

					// Если токен не удалось обновить, выйдем из системы
					await this.logout()
					return
				}

				//this.#refreshTokenJobInProcess = isRefreshed
			}
		}

		if (navigator.locks) {
			// для корректной работы с несколькими вкладками
			void navigator.locks.request("schedule_refresh_token_job", job)
		} else {
			void job()
		}
	}

	async checkIfIpWasChanged() {

		if (!this._userInfo || !this._authIp) return false

		const curIp = await this.getCurrentIp()
		this._isIpChanged = curIp !== this._authIp
		return this._isIpChanged
	}

	updateActivityDate() {
		this.#lastActivityDate = new Date()
		localStorage.setItem("last_activity", this.#lastActivityDate.toString())
	}

	isUserInactive() {
		const curDate = new Date()
		const dt = (+curDate) - (+this.#lastActivityDate)
		return dt > maxInactivityTime
	}

	hasAccessTo(objects: string | SecurityObject | SecurityObject[],
				secureOp: "and" | "or" = "and") {
		return hasAccessTo(objects, secureOp)
	}

	async getCurrentIp() {
		try {
			const response = await api.get("ip")
			return response.data
		} catch (err) {
			throw new Error(await getErrorMessage(err))
		}
	}

	#updateTokenDates() {
		const date = new Date()
		localStorage.setItem("last_token_refreshing", date.toString())

		const tokenExpDateStr = localStorage.getItem("token_exp_date")

		if (tokenExpDateStr) {
			localStorage.setItem("prev_token_exp_date", tokenExpDateStr)
		}

		date.setTime(date.getTime() + ttlMs)
		localStorage.setItem("token_exp_date", date.toString())
	}
}

export const userSessionStore = new UserSessionStore()
